(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root && root.customElements && !root.customElements.get('complementary-products-carousel')) {
    class ComplementaryProductsCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-complementary-track]');
        this.navigation = this.querySelector('[data-complementary-navigation]');
        this.previousButton = this.querySelector('[data-complementary-previous]');
        this.nextButton = this.querySelector('[data-complementary-next]');
        this.heading = this.querySelector('.complementary-carousel--title');

        if (!this.track || !this.dataset.productId) return;

        const drawerCart = root.document.querySelector('cart-element[data-view="drawer"]');
        this.excludedHandles = new Set(api.getCartProductHandles(drawerCart));

        this.onPrevious = () => this.scrollBy(-1);
        this.onNext = () => this.scrollBy(1);
        this.onTrackClick = (event) => {
          const quickAdd = event.target.closest?.('product-quick-add[data-form="false"]');
          if (!quickAdd || !this.track.contains(quickAdd)) return;

          const card = quickAdd.closest('[data-product-item]');
          this.pendingDirectAddHandle = card?.dataset?.handle || '';
        };
        this.onCartProductAdded = (event) => this.removeAddedProduct(event.detail);
        this.onCartUpdated = (event) => {
          const currentCart = event.detail || root.document.querySelector('cart-element[data-view="drawer"]');
          this.excludedHandles = new Set(api.buildExcludedHandles(
            api.getCartProductHandles(currentCart),
            this.pendingDirectAddHandle
          ));
          this.pendingDirectAddHandle = '';
          this.refreshRecommendations();
        };
        this.onScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = root.requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.updateNavigation();
          });
        };

        this.previousButton?.addEventListener('click', this.onPrevious);
        this.nextButton?.addEventListener('click', this.onNext);
        this.track.addEventListener('click', this.onTrackClick);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        root.addEventListener('theme:cart:productAdded', this.onCartProductAdded);
        root.addEventListener('theme:cart:updated', this.onCartUpdated);

        if (root.ResizeObserver) {
          this.resizeObserver = new root.ResizeObserver(() => this.updateNavigation());
          this.resizeObserver.observe(this.track);
        } else {
          this.onResize = () => this.updateNavigation();
          root.addEventListener('resize', this.onResize);
        }

        if (this.track.children.length) {
          this.reveal();
          this.updateNavigation();
          return;
        }

        // The empty section is visually hidden until recommendations arrive, so
        // it cannot be used as an IntersectionObserver target. Fetch immediately;
        // the response is small and Shopify applies the eligibility filtering.
        this.loadRecommendations();
      }

      disconnectedCallback() {
        this.previousButton?.removeEventListener('click', this.onPrevious);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.track?.removeEventListener('click', this.onTrackClick);
        this.track?.removeEventListener('scroll', this.onScroll);
        root.removeEventListener('theme:cart:productAdded', this.onCartProductAdded);
        root.removeEventListener('theme:cart:updated', this.onCartUpdated);
        this.resizeObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        if (this.onResize) root.removeEventListener('resize', this.onResize);
        if (this.scrollFrame) root.cancelAnimationFrame(this.scrollFrame);
      }

      loadWhenVisible() {
        if (!root.IntersectionObserver) {
          this.loadRecommendations();
          return;
        }

        this.visibilityObserver = new root.IntersectionObserver(
          (entries, observer) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            this.loadRecommendations();
          },
          { rootMargin: '600px 0px' }
        );
        this.visibilityObserver.observe(this);
      }

      async loadRecommendations({ force = false } = {}) {
        if (this.loading) {
          if (force) this.reloadAfterLoad = true;
          return;
        }
        if (this.loaded && !force) return;

        this.loading = true;
        if (force) this.loaded = false;

        try {
          const baseUrl = root.Shopify?.routes?.product_recommendations ||
            `${root.Shopify?.routes?.root || '/'}recommendations/products`;
          const intents = String(this.dataset.intents || 'complementary,related')
            .split(',')
            .map((intent) => intent.trim())
            .filter(Boolean);
          const result = await api.loadFirstAvailableRecommendations(
            intents,
            async (intent) => {
              const url = api.buildRecommendationsUrl(
                baseUrl,
                this.dataset.responseSectionId || 'complementary-products-carousel',
                this.dataset.productId,
                Number(this.dataset.maxProducts) || 10,
                intent
              );
              const response = await root.fetch(url, { credentials: 'same-origin' });
              if (!response.ok) throw new Error(`Recommendation request failed (${response.status})`);

              const html = await response.text();
              const document = new root.DOMParser().parseFromString(html, 'text/html');
              const cards = Array.from(document.querySelectorAll(
                'complementary-products-carousel [data-complementary-track] > [data-product-item]'
              ));
              return api.filterRecommendationItems(cards, this.excludedHandles);
            }
          );

          if (this.reloadAfterLoad) return;

          const fragment = document.createDocumentFragment();
          result.items.forEach((card) => fragment.appendChild(card));
          this.track.replaceChildren(fragment);
          this.dataset.recommendationSource = result.source;
          this.loaded = true;

          const heading = api.getRecommendationHeading(result.source, {
            complementary: this.dataset.complementaryTitle,
            related: this.dataset.relatedTitle,
          });
          if (this.heading) this.heading.textContent = heading;
          this.track.setAttribute('aria-label', heading);

          if (!result.items.length) {
            this.setAttribute('aria-hidden', 'true');
            if (this.navigation) this.navigation.hidden = true;
            return;
          }

          this.reveal();
          this.updateNavigation();

          this.dispatchEvent(new CustomEvent('theme:section:load', { bubbles: true }));
        } catch (error) {
          console.warn('Complementary products could not be loaded.', error);
        } finally {
          this.loading = false;
          if (this.reloadAfterLoad && this.isConnected) {
            this.reloadAfterLoad = false;
            this.loadRecommendations({ force: true });
          }
        }
      }

      removeAddedProduct(variantId) {
        const normalizedVariantId = String(variantId || '');
        if (!normalizedVariantId) return;

        const quickAdd = Array.from(this.track.querySelectorAll('product-quick-add[data-id]'))
          .find((element) => element.dataset.id === normalizedVariantId);
        const card = quickAdd?.closest('[data-product-item]');
        if (!card) return;

        if (card.dataset.handle) this.excludedHandles.add(card.dataset.handle);
        card.remove();
        if (!this.track.children.length) this.setAttribute('aria-hidden', 'true');
        this.updateNavigation();
      }

      refreshRecommendations() {
        if (!this.isConnected) return;
        this.loadRecommendations({ force: true });
      }

      reveal() {
        this.setAttribute('aria-hidden', 'false');
      }

      scrollBy(direction) {
        const firstCard = this.track?.querySelector('[data-product-item]');
        if (!firstCard) return;

        const styles = root.getComputedStyle(this.track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        const step = api.getScrollStep(firstCard.getBoundingClientRect().width, gap);
        const reduceMotion = root.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        this.track.scrollBy({
          left: direction * step,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      }

      updateNavigation() {
        if (!this.track || !this.navigation) return;

        const showNavigation = api.shouldShowNavigation(this.track.clientWidth, this.track.scrollWidth);
        this.navigation.hidden = !showNavigation;
        if (!showNavigation) return;

        const state = api.getNavigationState(
          this.track.scrollLeft,
          this.track.clientWidth,
          this.track.scrollWidth
        );
        this.setButtonState(this.previousButton, state.atStart);
        this.setButtonState(this.nextButton, state.atEnd);
      }

      setButtonState(button, disabled) {
        if (!button) return;
        button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
      }
    }

    root.customElements.define('complementary-products-carousel', ComplementaryProductsCarousel);
  }

  return api;
})(typeof window !== 'undefined' ? window : null, function () {
  const EDGE_TOLERANCE = 1;

  function buildRecommendationsUrl(baseUrl, sectionId, productId, limit, intent = 'complementary') {
    const separator = baseUrl.includes('?') ? '&' : '?';
    const params = new URLSearchParams({
      section_id: sectionId,
      product_id: productId,
      limit: String(limit),
      intent: String(intent),
    });
    return `${baseUrl}${separator}${params.toString()}`;
  }

  async function loadFirstAvailableRecommendations(intents, load) {
    for (const intent of intents || []) {
      try {
        const items = await load(intent);
        if (items?.length) return { source: intent, items };
      } catch (_error) {
        // Continue to the fallback source. If both requests fail, the section
        // remains hidden and the core product page is unaffected.
      }
    }

    return { source: 'none', items: [] };
  }

  function getRecommendationHeading(source, headings = {}) {
    if (source === 'related') return headings.related || 'You might also like';
    return headings.complementary || 'Complete your uniform';
  }

  function getProductHandleFromUrl(value) {
    const match = String(value || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function getCartProductHandles(cart) {
    if (!cart?.querySelectorAll) return [];

    const handles = Array.from(cart.querySelectorAll('.cart--item--title a[href*="/products/"]'))
      .map((link) => getProductHandleFromUrl(link.getAttribute('href')))
      .filter(Boolean);
    return Array.from(new Set(handles));
  }

  function filterRecommendationItems(items, excludedHandles) {
    const excluded = excludedHandles instanceof Set
      ? excludedHandles
      : new Set(excludedHandles || []);
    return Array.from(items || []).filter((item) => !excluded.has(item?.dataset?.handle));
  }

  function buildExcludedHandles(cartHandles, pendingDirectAddHandle) {
    const handles = new Set(Array.from(cartHandles || []).filter(Boolean));
    if (pendingDirectAddHandle) handles.add(String(pendingDirectAddHandle));
    return Array.from(handles);
  }

  function getScrollStep(cardWidth, gap) {
    return cardWidth + gap;
  }

  function shouldShowNavigation(clientWidth, scrollWidth) {
    return scrollWidth - clientWidth > EDGE_TOLERANCE;
  }

  function getNavigationState(scrollLeft, clientWidth, scrollWidth) {
    return {
      atStart: scrollLeft <= EDGE_TOLERANCE,
      atEnd: scrollLeft + clientWidth >= scrollWidth - EDGE_TOLERANCE,
    };
  }

  return {
    buildRecommendationsUrl,
    buildExcludedHandles,
    filterRecommendationItems,
    getCartProductHandles,
    getRecommendationHeading,
    loadFirstAvailableRecommendations,
    getScrollStep,
    shouldShowNavigation,
    getNavigationState,
  };
});
